/**
 * STORY-520: which model actually answered a chat turn.
 *
 * In AUTO mode the backend may move on to the next provider when the first
 * one fails. It then names the provider that answered in
 * `stats.answered_by` ({provider, model}) and the providers it moved off in
 * `stats.failover_from` (registration names, e.g. "gigachat-Model-A"). The
 * bubble label must name the model that answered, not the default one.
 */

type StatsLike = Record<string, unknown> | null | undefined;

interface ResponseLike {
  stats?: StatsLike;
  token_usage?: { model?: string | null; provider?: string | null } | null;
}

/** The model named by `stats.answered_by`, if the backend sent one. */
export function answeredByModel(response: ResponseLike): string | undefined {
  const answered = response.stats?.answered_by as
    | { model?: unknown; provider?: unknown }
    | undefined;
  if (!answered || typeof answered !== 'object') return undefined;
  if (typeof answered.model === 'string' && answered.model) return answered.model;
  if (typeof answered.provider === 'string' && answered.provider) return answered.provider;
  return undefined;
}

/** The providers AUTO mode moved off before this answer (may be empty). */
export function failoverFrom(response: ResponseLike): string[] {
  const raw = response.stats?.failover_from;
  if (!Array.isArray(raw)) return [];
  return raw.filter((n): n is string => typeof n === 'string' && n.length > 0);
}

/**
 * A registration name ("<type>-<model>") shown as its model part — the
 * backend's own key format; a bare name is shown as it is.
 */
export function providerDisplayName(name: string): string {
  const dash = name.indexOf('-');
  return dash > 0 && dash < name.length - 1 ? name.slice(dash + 1) : name;
}
